/**
 * Messages shown inside async react-select menus.
 *
 * Async selects fetch their options from the server only after the user types, so on
 * first open react-select falls back to its stock "No options" text. That reads like
 * "there is no data" instead of "search first", which leaves users stuck. These helpers
 * replace it with a prompt while the input is empty and a proper empty-result message
 * once a search has actually run.
 */

const DEFAULT_ENTITY_LABEL = "results";

/**
 * Builds a `noOptionsMessage` handler for an async select.
 *
 * @param entityLabel Plural noun for what is being searched, e.g. `"customers"`.
 *                    Omit it for a generic message.
 */
export const asyncSelectNoOptionsMessage =
  (entityLabel?: string) =>
  ({ inputValue }: { inputValue: string }): string => {
    const searchTerm = inputValue?.trim();

    if (!searchTerm) {
      return entityLabel ? `Start typing to search ${entityLabel}` : "Start typing to search";
    }

    return `No ${entityLabel ?? DEFAULT_ENTITY_LABEL} found for "${searchTerm}"`;
  };

/** Default handler used by FormBuilder and the header search when a field sets none. */
export const defaultAsyncSelectNoOptionsMessage = asyncSelectNoOptionsMessage();

/** Prompt for the customer lookups used across reports, invoicing and the header search. */
export const customerSearchNoOptionsMessage = asyncSelectNoOptionsMessage("customers");

/** Prompt for the city lookups used in the communications screens. */
export const citySearchNoOptionsMessage = asyncSelectNoOptionsMessage("cities");
